import {
  BiometricsData,
  BridgeMessage,
  ClipboardData,
  IntercomAction,
  LoginCredentials,
  MessagePayload,
  MessageType,
  PaymentDetails,
  PaymentResult,
  ShareContent,
  ShareResult,
  UserBiometricsData,
} from "@shared/services/webview/types";
import { UserProperties } from "@shared/types";
import { IS_WEB_VIEW } from "common/constants.ts";

export const postMessageToNative = <T = MessagePayload>(
  message: Partial<BridgeMessage<T>>
): void => {
  if (!IS_WEB_VIEW) return;
  if (window.postMessageToNative) {
    const fullMessage = {
      id: window.generateId(),
      ...message,
    } as BridgeMessage<T>;
    window.postMessageToNative(fullMessage);
  } else {
    console.error(
      "WebView (React): window.postMessageToNative is not available."
    );
  }
};

export const sendMessageToNativeAndWaitForResponse = <
  RequestPayload = MessagePayload,
  ResponsePayload = MessagePayload
>(
  type: MessageType | string,
  payload?: RequestPayload
): Promise<ResponsePayload> => {
  if (IS_WEB_VIEW && window.sendMessageToNativeAndWaitForResponse) {
    return window.sendMessageToNativeAndWaitForResponse<
      RequestPayload,
      ResponsePayload
    >(type, payload);
  } else {
    console.error(
      `WebView (React): window.sendMessageToNativeAndWaitForResponse is not available for ${type}.`
    );
    return Promise.reject(new Error("Bridge to native not available."));
  }
};

export const requestBiometricLogin = async (): Promise<LoginCredentials> => {
  return sendMessageToNativeAndWaitForResponse<undefined, LoginCredentials>(
    MessageType.BIOMETRIC_LOGIN_REQUEST
  );
};

export const toggleUserBiometric = async (): Promise<
  UserBiometricsData | undefined
> => {
  return sendMessageToNativeAndWaitForResponse<
    undefined,
    UserBiometricsData | undefined
  >(MessageType.TOGGLE_USER_BIOMETRIC);
};

export const requestBiometricDisplayData =
  async (): Promise<BiometricsData> => {
    return sendMessageToNativeAndWaitForResponse<undefined, BiometricsData>(
      MessageType.GET_BIOMETRIC_DATA
    );
  };

export const initiatePayment = async (
  details: PaymentDetails
): Promise<PaymentResult> => {
  return sendMessageToNativeAndWaitForResponse<PaymentDetails, PaymentResult>(
    MessageType.INITIATE_PAYMENT_REQUEST,
    details
  );
};

export const shareContent = async (
  content: ShareContent
): Promise<ShareResult> => {
  return sendMessageToNativeAndWaitForResponse<ShareContent, ShareResult>(
    MessageType.SHARE_REQUEST,
    content
  );
};

export const triggerSaveBiometric = async (
  credentials: LoginCredentials
): Promise<void> => {
  return postMessageToNative({
    type: MessageType.TRIGGER_SAVE_BIOMETRICS,
    payload: credentials,
  });
};

export const userUpdated = async (
  user: UserProperties | undefined
): Promise<void> => {
  return postMessageToNative({
    type: MessageType.USER_UPDATED,
    payload: user,
  });
};

export const requestLeaveReview = async (): Promise<void> => {
  return sendMessageToNativeAndWaitForResponse<undefined, void>(
    MessageType.LEAVE_REVIEW
  );
};

export const requestIntercomAction = async (
  actionData: IntercomAction
): Promise<void> => {
  return postMessageToNative({
    type: MessageType.REQUEST_INTERCOM_ACTION,
    payload: actionData,
  });
};

export const requestClipboardData = async (): Promise<ClipboardData> => {
  return sendMessageToNativeAndWaitForResponse<void, ClipboardData>(
    MessageType.REQUEST_CLIPBOARD_DATA
  );
};

export const themeUpdated = async (isDarkMode: boolean): Promise<void> => {
  return postMessageToNative({
    type: MessageType.THEME_UPDATED,
    payload: { isDarkMode },
  });
};
